import { useState } from 'react';
import { errorMessage } from '@/core/http';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Page,
  PageHeader,
  Skeleton,
  Textarea,
} from '@/core/ui';
import { CaretRight, ChatCircle, Plus, Sparkle } from '@/core/ui/icons';
import { useCreateFlow, useFlows } from '../application/queries';
import {
  DESCRIPTION_MAX,
  flowNameProblem,
  flowStatus,
  formatDateTime,
  NAME_MAX,
  sessionsLabel,
} from '../domain/flow';
import type { FlowSummary } from '../domain/types';
import { EntrySettings } from './EntrySettings';

interface Props {
  onOpen: (flowId: string) => void;
}

/** Fluxos da Centuria: a lista, o "Novo fluxo" e a entrada (boas-vindas e atalhos). */
export function FlowsPage({ onOpen }: Props) {
  const flows = useFlows();
  const [creating, setCreating] = useState(false);
  const list = flows.data ?? [];

  return (
    <Page>
      <PageHeader
        title="Fluxos da Centuria"
        description="Conversas guiadas no portal: perguntas, respostas e caminhos que terminam numa aula, numa visita ou na IA."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden className="size-4" />
            Novo fluxo
          </Button>
        }
      />

      {flows.isPending ? (
        <Skeleton className="h-60 rounded-3xl" />
      ) : flows.isError ? (
        <ErrorState error={flows.error} onRetry={() => void flows.refetch()} />
      ) : list.length === 0 ? (
        <EmptyState
          title="Nenhum fluxo ainda"
          description="Crie o primeiro: um card de início com uma saudação já vem pronto."
          action={<Button onClick={() => setCreating(true)}>Criar fluxo</Button>}
        />
      ) : (
        <ul aria-label="Fluxos" className="overflow-hidden rounded-3xl bg-surface">
          {list.map((f) => (
            <Row key={f.id} flow={f} onOpen={() => onOpen(f.id)} />
          ))}
        </ul>
      )}

      <div className="mt-8">
        <EntrySettings flows={list} />
      </div>

      <NewFlowDialog open={creating} onClose={() => setCreating(false)} onCreated={onOpen} />
    </Page>
  );
}

function Row({ flow: f, onOpen }: { flow: FlowSummary; onOpen: () => void }) {
  return (
    <li className="[&+li]:mt-px">
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-surface-2 sm:px-6"
      >
        <span className="hidden size-11 shrink-0 items-center justify-center rounded-2xl bg-nav-active text-nav-active-fg sm:flex">
          {f.is_welcome ? (
            <Sparkle aria-hidden className="size-5" />
          ) : (
            <ChatCircle aria-hidden className="size-5" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-base font-medium text-fg">{f.name}</span>
            {f.is_welcome && <Badge tone="brand">Boas-vindas</Badge>}
            {f.is_shortcut && <Badge tone="brand">Atalho</Badge>}
          </p>
          {f.description && <p className="mt-0.5 line-clamp-1 text-sm text-fg-subtle">{f.description}</p>}
          <p className="mt-1 text-xs text-fg-subtle">
            {sessionsLabel(f.sessions)} · atualizado em {formatDateTime(f.updated_at)}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {flowStatus(f).map((s) => (
            <Badge key={s.label} tone={s.tone} className="whitespace-nowrap">
              {s.label}
            </Badge>
          ))}
        </div>
        <CaretRight
          aria-hidden
          className="size-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-gray-500"
        />
      </button>
    </li>
  );
}

function NewFlowDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (flowId: string) => void;
}) {
  const create = useCreateFlow();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [touched, setTouched] = useState(false);
  const problem = flowNameProblem(name);

  const close = () => {
    setName('');
    setDescription('');
    setTouched(false);
    create.reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Novo fluxo"
      description="Começa com um card de início e uma saudação."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (problem) return;
          create.mutate(
            { name: name.trim(), description: description.trim() },
            { onSuccess: (flow) => onCreated(flow.id) },
          );
        }}
      >
        <Field label="Nome" error={touched && problem ? problem : undefined}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              value={name}
              maxLength={NAME_MAX}
              placeholder="ex.: Quero uma visita"
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Descrição (opcional)" hint="Só para o time — o portal não mostra.">
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              value={description}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        {create.isError && (
          <p role="alert" className="text-sm text-fg">
            {errorMessage(create.error)}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" loading={create.isPending}>
            Criar e abrir
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
